import React, { useEffect, useState } from "react";
import {
  ActivityIndicator,
  FlatList,
  StyleSheet,
  Text,
  TextInput,
  View,
} from "react-native";
import { useReferenceWindowDimensions } from "../utils/referenceViewport";
import { SafeAreaView } from "react-native-safe-area-context";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { useQuery } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react-native";
import { RootStackParamList } from "../navigation/types";
import {
  AdminSession,
  AdminUser,
  addAdminLibrary,
  createAdminUser,
  getAdminConfiguration,
  getAdminOverview,
  getAdminSectionRecords,
  runAdminGet,
  runAdminDelete,
  runAdminPost,
  removeAdminLibrary,
  saveAdminConfiguration,
  updateAdminUserPolicy,
  updateAdminUserPassword,
} from "../api/jellyfin";
import { useSession } from "../session/SessionProvider";
import { FocusablePressable } from "../components/FocusablePressable";

type Props = NativeStackScreenProps<RootStackParamList, "Admin">;

const NAV_ITEMS = [
  ["overview", "Dashboard"],
  ["settings", "General"],
  ["branding", "Branding"],
  ["users", "Users"],
  ["libraries", "Libraries"],
  ["libraries/display", "Library Display"],
  ["libraries/metadata", "Library Metadata"],
  ["libraries/nfo", "NFO"],
  ["playback", "Playback Diagnostics"],
  ["playback/transcoding", "Transcoding"],
  ["playback/resume", "Resume"],
  ["playback/streaming", "Streaming"],
  ["playback/trickplay", "Trickplay"],
  ["devices", "Devices"],
  ["sessions", "Sessions"],
  ["activity", "Activity"],
  ["livetv", "Live TV"],
  ["livetv/recordings", "DVR"],
  ["plugins", "Plugins"],
  ["plugins/repositories", "Repositories"],
  ["networking", "Networking"],
  ["keys", "API Keys"],
  ["backups", "Backups"],
  ["logs", "Logs"],
  ["tasks", "Scheduled Tasks"],
  ["advanced/api", "API Console"],
] as const;

const NAV_GROUPS: Partial<Record<(typeof NAV_ITEMS)[number][0], string>> = {
  overview: "SERVER",
  devices: "DEVICES",
  livetv: "LIVE TV",
  plugins: "PLUGINS",
  networking: "ADVANCED",
};

const CONFIG_FIELDS: Record<string, string[]> = {
  settings: [
    "ServerName",
    "UICulture",
    "CachePath",
    "MetadataPath",
    "PreferredMetadataLanguage",
    "MetadataCountryCode",
    "EnableMetrics",
    "EnableSlowResponseWarning",
    "LogFileRetentionDays",
    "InactiveSessionThreshold",
    "LibraryMonitorDelay",
    "MinResumePct",
    "MaxResumePct",
    "MinResumeDurationSeconds",
  ],
  branding: ["LoginDisclaimer", "CustomCss", "SplashscreenEnabled"],
  networking: [
    "BaseUrl",
    "EnableHttps",
    "RequireHttps",
    "InternalHttpPort",
    "InternalHttpsPort",
    "PublicHttpPort",
    "PublicHttpsPort",
    "AutoDiscovery",
    "EnableUPnP",
    "EnableIPv4",
    "EnableIPv6",
    "EnableRemoteAccess",
    "LocalNetworkSubnets",
    "LocalNetworkAddresses",
    "KnownProxies",
  ],
  "playback/transcoding": [
    "TranscodingTempPath",
    "EncodingThreadCount",
    "HardwareAccelerationType",
    "EnableHardwareEncoding",
    "EnableThrottling",
    "ThrottleDelaySeconds",
    "EnableSegmentDeletion",
    "SegmentKeepSeconds",
    "H264Crf",
    "H265Crf",
    "EncoderPreset",
    "EnableSubtitleExtraction",
  ],
  "playback/resume": [
    "MinResumePct",
    "MaxResumePct",
    "MinResumeDurationSeconds",
    "MinAudiobookResume",
    "MaxAudiobookResume",
  ],
  "playback/trickplay": ["TrickplayOptions"],
};

function isActive(session: AdminSession) {
  const activity = Date.parse(session.LastActivityDate ?? "");
  return Boolean(
    session.NowPlayingItem ||
    (Number.isFinite(activity) && Date.now() - activity < 15 * 60 * 1000),
  );
}

function Stat({
  label,
  value,
  detail,
  tone,
}: {
  label: string;
  value: string | number;
  detail?: string;
  tone?: "cyan" | "green" | "amber";
}) {
  return (
    <View
      style={[
        styles.stat,
        tone === "cyan" && styles.statCyan,
        tone === "green" && styles.statGreen,
        tone === "amber" && styles.statAmber,
      ]}
    >
      <Text style={styles.statLabel}>{label.toUpperCase()}</Text>
      <Text numberOfLines={1} style={styles.statValue}>
        {value}
      </Text>
      {detail ? (
        <Text numberOfLines={1} style={styles.statDetail}>
          {detail}
        </Text>
      ) : null}
    </View>
  );
}

function Panel({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <View style={styles.panel}>
      <Text style={styles.panelTitle}>{title}</Text>
      {description ? (
        <Text style={styles.panelDescription}>{description}</Text>
      ) : null}
      <View style={styles.panelBody}>{children}</View>
    </View>
  );
}

function AdminAction({
  label,
  onPress,
  warning = false,
  confirmed = false,
}: {
  label: string;
  onPress: () => void;
  warning?: boolean;
  confirmed?: boolean;
}) {
  return (
    <FocusablePressable
      onPress={onPress}
      style={(focused, pressed, hovered) => [
        styles.actionButton,
        warning && styles.actionButtonWarning,
        confirmed && styles.actionButtonConfirmed,
        (focused || hovered) && styles.actionButtonActive,
        pressed && styles.pressed,
      ]}
    >
      <Text style={[styles.actionText, warning && styles.actionTextWarning]}>
        {label}
      </Text>
    </FocusablePressable>
  );
}

function fieldLabel(key: string) {
  return key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z])([A-Z][a-z])/g, "$1 $2")
    .toUpperCase();
}

function displayConfigValue(value: unknown) {
  if (Array.isArray(value)) return value.join(", ");
  if (value && typeof value === "object")
    return JSON.stringify(value, null, 2);
  return value === undefined || value === null ? "" : String(value);
}

function parseConfigValue(text: string, original: unknown) {
  if (typeof original === "number") {
    const parsed = Number(text);
    return Number.isFinite(parsed) ? parsed : original;
  }
  if (Array.isArray(original))
    return text
      .split(",")
      .map((entry) => entry.trim())
      .filter(Boolean);
  if (original && typeof original === "object") {
    try {
      return JSON.parse(text) as unknown;
    } catch {
      return original;
    }
  }
  return text;
}

function ConfigurationEditor({
  section,
  document,
  saving,
  onSave,
}: {
  section: string;
  document: Record<string, unknown>;
  saving: boolean;
  onSave: (value: Record<string, unknown>) => void;
}) {
  const fields = (CONFIG_FIELDS[section] ?? []).filter((key) =>
    Object.prototype.hasOwnProperty.call(document, key),
  );
  const [draft, setDraft] = useState<Record<string, string>>({});
  useEffect(() => {
    setDraft(
      Object.fromEntries(
        fields.map((key) => [key, displayConfigValue(document[key])]),
      ),
    );
  }, [document, section]);
  return (
    <View style={styles.configEditor}>
      <FlatList
        data={fields}
        keyExtractor={(key) => key}
        scrollEnabled={false}
        renderItem={({ item: key }) => {
          const original = document[key];
          if (typeof original === "boolean")
            return (
              <View style={styles.configBoolean}>
                <View style={styles.sessionCopy}>
                  <Text style={styles.key}>{fieldLabel(key)}</Text>
                  <Text style={styles.sessionMeta}>
                    {original ? "Enabled" : "Disabled"}
                  </Text>
                </View>
                <AdminAction
                  label={original ? "On" : "Off"}
                  onPress={() =>
                    onSave({ ...document, [key]: !original })
                  }
                />
              </View>
            );
          return (
            <View style={styles.configField}>
              <Text style={styles.key}>{fieldLabel(key)}</Text>
              <TextInput
                value={draft[key] ?? ""}
                onChangeText={(value) =>
                  setDraft((current) => ({ ...current, [key]: value }))
                }
                multiline={Boolean(original && typeof original === "object")}
                selectionColor="#67e8f9"
                autoCapitalize="none"
                autoCorrect={false}
                style={[
                  styles.configInput,
                  original && typeof original === "object"
                    ? styles.configInputMultiline
                    : null,
                ]}
              />
            </View>
          );
        }}
      />
      <AdminAction
        label={saving ? "Saving..." : "Save configuration"}
        onPress={() =>
          onSave({
            ...document,
            ...Object.fromEntries(
              fields
                .filter((key) => typeof document[key] !== "boolean")
                .map((key) => [
                  key,
                  parseConfigValue(draft[key] ?? "", document[key]),
                ]),
            ),
          })
        }
      />
    </View>
  );
}

function previewRecord(record: Record<string, unknown>) {
  const title = String(
    record.Name ?? record.name ?? record.Id ?? record.id ?? "Entry",
  );
  const detailValue =
    record.Value ??
    record.Status ??
    record.Version ??
    record.Type ??
    record.Overview ??
    record.Path;
  let detail = "No additional details";
  if (
    typeof detailValue === "string" ||
    typeof detailValue === "number" ||
    typeof detailValue === "boolean"
  )
    detail = String(detailValue);
  else if (detailValue !== undefined) detail = JSON.stringify(detailValue);
  return { title, detail };
}

export function AdminScreen({ navigation, route }: Props) {
  const { width } = useReferenceWindowDimensions();
  const compact = width < 900;
  const { credentials, user } = useSession();
  const [selected, setSelected] = useState(route.params?.section || "overview");
  const [consolePath, setConsolePath] = useState("/System/Info");
  const [consoleResult, setConsoleResult] = useState("");
  const [consolePending, setConsolePending] = useState(false);
  const [actionPending, setActionPending] = useState("");
  const [actionMessage, setActionMessage] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [newUserName, setNewUserName] = useState("");
  const [newUserPassword, setNewUserPassword] = useState("");
  const [passwordUser, setPasswordUser] = useState<AdminUser | null>(null);
  const [passwordDraft, setPasswordDraft] = useState("");
  const [apiKeyName, setApiKeyName] = useState("");
  const [libraryName, setLibraryName] = useState("");
  const [libraryType, setLibraryType] = useState("mixed");
  const [libraryPath, setLibraryPath] = useState("");
  const usesOverviewData =
    selected === "overview" ||
    selected === "users" ||
    selected.startsWith("libraries") ||
    selected === "sessions" ||
    selected === "devices" ||
    selected === "tasks";
  const overview = useQuery({
    queryKey: ["admin", "overview"],
    queryFn: () => getAdminOverview(credentials!),
    enabled: Boolean(credentials && user?.Policy?.IsAdministrator),
    refetchInterval: 30_000,
  });
  const sectionQuery = useQuery({
    queryKey: ["admin", "section", selected],
    queryFn: () => getAdminSectionRecords(credentials!, selected),
    enabled: Boolean(
      credentials &&
      user?.Policy?.IsAdministrator &&
      !usesOverviewData &&
      selected !== "advanced/api",
    ),
  });
  const configQuery = useQuery({
    queryKey: ["admin", "configuration", selected],
    queryFn: () => getAdminConfiguration(credentials!, selected),
    enabled: Boolean(
      credentials &&
      user?.Policy?.IsAdministrator &&
      CONFIG_FIELDS[selected],
    ),
  });
  if (!credentials) return null;
  if (user && !user.Policy?.IsAdministrator)
    return (
      <SafeAreaView style={styles.safe}>
        <View style={styles.center}>
          <Text style={styles.errorTitle}>Administrator access required</Text>
          <Text style={styles.errorCopy}>
            Server controls are only available to Jellyfin administrators.
          </Text>
          <FocusablePressable
            onPress={() => navigation.navigate("Home")}
            style={styles.returnButton}
          >
            <Text style={styles.returnText}>Return to home</Text>
          </FocusablePressable>
        </View>
      </SafeAreaView>
    );
  if (overview.isLoading || !overview.data)
    return (
      <SafeAreaView style={styles.safe}>
        <View style={styles.center}>
          <ActivityIndicator color="#67e8f9" size="large" />
          <Text style={styles.loading}>Loading server controls...</Text>
        </View>
      </SafeAreaView>
    );
  const data = overview.data;
  const performAction = async (
    key: string,
    action: () => Promise<unknown>,
    success: string,
  ) => {
    if (actionPending) return;
    setActionPending(key);
    setActionMessage("");
    try {
      await action();
      setActionMessage(success);
      setConfirmation("");
      await overview.refetch();
    } catch (error) {
      setActionMessage(
        error instanceof Error ? error.message : "The server action failed.",
      );
    } finally {
      setActionPending("");
    }
  };
  const confirmAction = (
    key: string,
    action: () => Promise<unknown>,
    success: string,
  ) => {
    if (confirmation !== key) {
      setConfirmation(key);
      setActionMessage("Press the highlighted action again to confirm.");
      return;
    }
    void performAction(key, action, success);
  };
  const toggleUser = (
    entry: AdminUser,
    field: "IsDisabled" | "IsAdministrator",
    value: boolean,
  ) => {
    const key = `${field === "IsDisabled" ? "enabled" : "admin"}-${entry.Id}`;
    const risky = value || field === "IsAdministrator";
    const action = () =>
      updateAdminUserPolicy(credentials, entry, { [field]: value });
    const success = `${entry.Name ?? "User"} updated.`;
    if (risky) confirmAction(key, action, success);
    else void performAction(key, action, success);
  };
  const activeSessions = data.sessions.filter(isActive);
  const playing = data.sessions.filter((session) =>
    Boolean(session.NowPlayingItem && !session.PlayState?.IsPaused),
  );
  const transcoding = data.sessions.filter((session) =>
    Boolean(
      session.TranscodingInfo &&
      (!session.TranscodingInfo.IsVideoDirect ||
        !session.TranscodingInfo.IsAudioDirect),
    ),
  );
  const runningTasks = data.tasks.filter((task) => task.State === "Running");
  const failedTasks = data.tasks.filter((task) =>
    String(task.LastExecutionResult?.Status ?? "")
      .toLowerCase()
      .includes("fail"),
  );
  const mediaItems = Object.values(data.counts).reduce(
    (total, value) => total + (Number(value) || 0),
    0,
  );
  const stats = [
    {
      label: "Server",
      value: data.system.ServerName ?? "Server",
      detail: data.system.HasPendingRestart
        ? "Restart pending"
        : (data.system.OperatingSystemDisplayName ??
          data.system.OperatingSystem ??
          "Unknown"),
      tone: "cyan" as const,
    },
    {
      label: "Media Items",
      value: mediaItems || "Unknown",
      detail: `${data.libraries.length} libraries`,
    },
    {
      label: "Active Users",
      value: new Set(
        activeSessions.map((session) => session.UserName).filter(Boolean),
      ).size,
      detail: `${data.users.length} total users`,
      tone: activeSessions.length ? ("green" as const) : undefined,
    },
    {
      label: "Active Playback",
      value: playing.length,
      detail: `${activeSessions.length} active sessions`,
      tone: playing.length ? ("green" as const) : undefined,
    },
    {
      label: "Transcoding",
      value: transcoding.length,
      detail: transcoding.length ? "Watch server load" : "Direct play or idle",
      tone: transcoding.length ? ("amber" as const) : ("green" as const),
    },
    {
      label: "Tasks",
      value: runningTasks.length,
      detail: failedTasks.length
        ? `${failedTasks.length} failed last run`
        : "No failures reported",
      tone: failedTasks.length ? ("amber" as const) : ("green" as const),
    },
  ];
  const selectedLabel =
    NAV_ITEMS.find(([id]) => id === selected)?.[1] ?? "Dashboard";
  const content =
    selected === "overview"
      ? ["sessions", "system", "tasks", "recent-activity"]
      : selected === "users"
        ? ["users"]
        : selected.startsWith("libraries")
          ? ["libraries"]
          : selected === "sessions" || selected === "devices"
            ? ["all-sessions"]
            : selected === "tasks"
              ? ["all-tasks"]
              : selected === "keys"
                ? ["api-keys"]
              : selected === "advanced/api"
                ? ["api-console"]
                : ["generic"];
  return (
    <SafeAreaView style={styles.safe} edges={[]}>
      <View style={[styles.shell, compact && styles.shellCompact]}>
        <View style={[styles.sidebar, compact && styles.sidebarCompact]}>
          <FocusablePressable
            onPress={() => navigation.navigate("Home")}
            style={styles.brandButton}
          >
            <Text style={styles.brand}>JELLYFLIX</Text>
          </FocusablePressable>
          <Text style={styles.adminLabel}>JELLYFIN ADMINISTRATION</Text>
          <FlatList
            horizontal={compact}
            data={NAV_ITEMS}
            keyExtractor={(item) => item[0]}
            showsHorizontalScrollIndicator={false}
            showsVerticalScrollIndicator={false}
            style={[styles.navList, compact && styles.navListCompact]}
            contentContainerStyle={[styles.nav, compact && styles.navCompact]}
            renderItem={({ item }) => (
              <View>
                {!compact && NAV_GROUPS[item[0]] ? (
                  <Text style={styles.navGroup}>{NAV_GROUPS[item[0]]}</Text>
                ) : null}
                <FocusablePressable
                  onPress={() => {
                    setSelected(item[0]);
                    navigation.replace(
                      "Admin",
                      item[0] === "overview" ? undefined : { section: item[0] },
                    );
                  }}
                  style={(focused, pressed, hovered) => [
                    styles.navItem,
                    selected === item[0] && styles.navItemSelected,
                    (focused || hovered) && styles.navItemActive,
                    pressed && styles.pressed,
                  ]}
                >
                  <Text
                    style={[
                      styles.navText,
                      selected === item[0] && styles.navTextSelected,
                    ]}
                  >
                    {item[1]}
                  </Text>
                </FocusablePressable>
              </View>
            )}
          />
        </View>
        <FlatList
          data={content}
          keyExtractor={(item) => item}
          contentContainerStyle={styles.content}
          ListHeaderComponent={
            <>
              <View style={styles.header}>
                <View style={styles.headerCopy}>
                  <Text style={styles.adminLabel}>JELLYFIN ADMINISTRATION</Text>
                  <Text style={styles.title}>{selectedLabel}</Text>
                  <Text style={styles.subtitle}>
                    {selected === "overview"
                      ? "A quick read on server health, library size, current clients, and work that needs attention."
                      : `${selectedLabel} administration and server state.`}
                  </Text>
                  <Text style={styles.loaded}>Last loaded just now</Text>
                  {actionMessage ? (
                    <Text style={styles.actionMessage}>{actionMessage}</Text>
                  ) : null}
                </View>
                <FocusablePressable
                  accessibilityLabel="Refresh dashboard"
                  onPress={() => void overview.refetch()}
                  style={(focused, pressed, hovered) => [
                    styles.refresh,
                    (focused || hovered) && styles.refreshActive,
                    pressed && styles.pressed,
                  ]}
                >
                  <RefreshCw color="#d5d5d5" size={16} />
                  <Text style={styles.refreshText}>
                    {overview.isFetching ? "Refreshing..." : "Refresh"}
                  </Text>
                </FocusablePressable>
              </View>
              {selected === "overview" ? (
                <FlatList
                  data={stats}
                  numColumns={compact ? 2 : width >= 1700 ? 6 : 3}
                  key={`stats-${compact ? 2 : width >= 1700 ? 6 : 3}`}
                  keyExtractor={(item) => item.label}
                  scrollEnabled={false}
                  columnWrapperStyle={styles.statRow}
                  contentContainerStyle={styles.stats}
                  renderItem={({ item }) => <Stat {...item} />}
                />
              ) : null}
            </>
          }
          renderItem={({ item }) =>
            item === "sessions" ? (
              <Panel
                title="Live Sessions"
                description="Clients active on the server right now."
              >
                {activeSessions.length ? (
                  <FlatList
                    data={activeSessions.slice(0, 6)}
                    keyExtractor={(session, index) =>
                      session.Id ?? `session-${index}`
                    }
                    scrollEnabled={false}
                    renderItem={({ item: session }) => (
                      <View style={[styles.session, compact && styles.sessionCompact]}>
                        <View style={[styles.sessionCopy, compact && styles.sessionCopyCompact]}>
                          <Text style={styles.sessionTitle}>
                            {session.NowPlayingItem?.Name ??
                              session.UserName ??
                              "Session"}
                          </Text>
                          <Text style={styles.sessionMeta}>
                            {[
                              session.UserName,
                              session.Client,
                              session.DeviceName,
                            ]
                              .filter(Boolean)
                              .join(" · ")}
                          </Text>
                        </View>
                        <Text style={styles.goodBadge}>
                          {session.NowPlayingItem
                            ? session.PlayState?.IsPaused
                              ? "PAUSED"
                              : "PLAYING"
                            : "ACTIVE"}
                        </Text>
                      </View>
                    )}
                  />
                ) : (
                  <Text style={styles.empty}>No active sessions.</Text>
                )}
              </Panel>
            ) : item === "system" ? (
              <Panel
                title="System"
                description="The fields admins usually need first: version, restart state, paths, and storage locations."
              >
                <View style={styles.keyValue}>
                  <Text style={styles.key}>VERSION</Text>
                  <Text style={styles.value}>
                    {data.system.Version ?? "Unknown"}
                  </Text>
                </View>
                <View style={styles.keyValue}>
                  <Text style={styles.key}>OPERATING SYSTEM</Text>
                  <Text style={styles.value}>
                    {data.system.OperatingSystemDisplayName ??
                      data.system.OperatingSystem ??
                      "Unknown"}
                  </Text>
                </View>
                <View style={styles.keyValue}>
                  <Text style={styles.key}>ARCHITECTURE</Text>
                  <Text style={styles.value}>
                    {data.system.SystemArchitecture ?? "Unknown"}
                  </Text>
                </View>
                <View style={styles.keyValue}>
                  <Text style={styles.key}>PROGRAM DATA</Text>
                  <Text numberOfLines={2} style={styles.value}>
                    {data.system.ProgramDataPath ?? "Unknown"}
                  </Text>
                </View>
                <View style={styles.systemActions}>
                  <AdminAction
                    label={
                      actionPending === "restart-server"
                        ? "Restarting..."
                        : confirmation === "restart-server"
                          ? "Confirm restart"
                          : "Restart server"
                    }
                    warning
                    confirmed={confirmation === "restart-server"}
                    onPress={() =>
                      confirmAction(
                        "restart-server",
                        () => runAdminPost(credentials, "/System/Restart"),
                        "The server restart was requested.",
                      )
                    }
                  />
                  <AdminAction
                    label={
                      actionPending === "shutdown-server"
                        ? "Shutting down..."
                        : confirmation === "shutdown-server"
                          ? "Confirm shutdown"
                          : "Shut down server"
                    }
                    warning
                    confirmed={confirmation === "shutdown-server"}
                    onPress={() =>
                      confirmAction(
                        "shutdown-server",
                        () => runAdminPost(credentials, "/System/Shutdown"),
                        "The server shutdown was requested.",
                      )
                    }
                  />
                </View>
              </Panel>
            ) : item === "tasks" ? (
              <Panel
                title="Running Tasks"
                description="Live maintenance jobs currently executing on the Jellyfin server."
              >
                {runningTasks.length ? (
                  <FlatList
                    data={runningTasks}
                    keyExtractor={(task, index) =>
                      task.Id ?? task.Key ?? `task-${index}`
                    }
                    scrollEnabled={false}
                    renderItem={({ item: task }) => (
                      <View style={[styles.session, compact && styles.sessionCompact]}>
                        <Text style={styles.sessionTitle}>
                          {task.Name ?? task.Key ?? "Task"}
                        </Text>
                        <Text style={styles.goodBadge}>RUNNING</Text>
                      </View>
                    )}
                  />
                ) : (
                  <Text style={styles.empty}>
                    No tasks running. Scheduled tasks are idle.
                  </Text>
                )}
              </Panel>
            ) : item === "recent-activity" ? (
              <Panel
                title="Recent Activity"
                description="The latest server events reported by Jellyfin."
              >
                {data.activity.length ? (
                  <FlatList
                    data={data.activity}
                    keyExtractor={(entry, index) =>
                      String(entry.Id ?? `activity-${index}`)
                    }
                    scrollEnabled={false}
                    renderItem={({ item: entry }) => (
                      <View style={[styles.session, compact && styles.sessionCompact]}>
                        <View style={[styles.sessionCopy, compact && styles.sessionCopyCompact]}>
                          <Text style={styles.sessionTitle}>
                            {entry.Name ?? entry.Type ?? "Server activity"}
                          </Text>
                          <Text numberOfLines={2} style={styles.sessionMeta}>
                            {entry.Overview ?? "No additional details"}
                          </Text>
                        </View>
                        <Text style={styles.activityDate}>
                          {entry.Date
                            ? new Date(entry.Date).toLocaleString()
                            : "RECENT"}
                        </Text>
                      </View>
                    )}
                  />
                ) : (
                  <Text style={styles.empty}>No recent activity.</Text>
                )}
              </Panel>
            ) : item === "users" ? (
              <Panel
                title="Users"
                description="Create accounts, reset passwords, and manage access policies."
              >
                <View style={styles.createUserForm}>
                  <View style={styles.formInputWrap}>
                    <Text style={styles.key}>NEW USER NAME</Text>
                    <TextInput
                      value={newUserName}
                      onChangeText={setNewUserName}
                      autoCapitalize="none"
                      autoCorrect={false}
                      selectionColor="#67e8f9"
                      style={styles.configInput}
                    />
                  </View>
                  <View style={styles.formInputWrap}>
                    <Text style={styles.key}>INITIAL PASSWORD</Text>
                    <TextInput
                      value={newUserPassword}
                      onChangeText={setNewUserPassword}
                      autoCapitalize="none"
                      autoCorrect={false}
                      secureTextEntry
                      selectionColor="#67e8f9"
                      style={styles.configInput}
                    />
                  </View>
                  <AdminAction
                    label={
                      actionPending === "create-user"
                        ? "Creating..."
                        : "Create user"
                    }
                    onPress={() =>
                      void performAction(
                        "create-user",
                        () =>
                          createAdminUser(
                            credentials,
                            newUserName,
                            newUserPassword,
                          ),
                        `${newUserName.trim() || "User"} created.`,
                      ).then(() => {
                        setNewUserName("");
                        setNewUserPassword("");
                      })
                    }
                  />
                </View>
                <FlatList
                  data={data.users}
                  keyExtractor={(entry, index) => entry.Id ?? `user-${index}`}
                  scrollEnabled={false}
                  renderItem={({ item: entry }) => (
                    <View style={[styles.session, compact && styles.sessionCompact]}>
                      <View style={[styles.sessionCopy, compact && styles.sessionCopyCompact]}>
                        <Text style={styles.sessionTitle}>
                          {entry.Name ?? "User"}
                        </Text>
                        <Text style={styles.sessionMeta}>
                          {entry.LastLoginDate
                            ? `Last login ${new Date(entry.LastLoginDate).toLocaleString()}`
                            : "No recent login"}
                        </Text>
                      </View>
                      <View style={[styles.rowActions, compact && styles.rowActionsCompact]}>
                        <Text
                          style={
                            entry.Policy?.IsDisabled
                              ? styles.warnBadge
                              : styles.infoBadge
                          }
                        >
                          {entry.Policy?.IsDisabled
                            ? "DISABLED"
                            : entry.Policy?.IsAdministrator
                              ? "ADMIN"
                              : "USER"}
                        </Text>
                        <AdminAction
                          label="Password"
                          confirmed={passwordUser?.Id === entry.Id}
                          onPress={() => {
                            setPasswordUser(entry);
                            setPasswordDraft("");
                          }}
                        />
                        <AdminAction
                          label={
                            actionPending === `enabled-${entry.Id}`
                              ? "Saving..."
                              : entry.Policy?.IsDisabled
                                ? "Enable"
                                : "Disable"
                          }
                          warning={!entry.Policy?.IsDisabled}
                          confirmed={
                            confirmation === `enabled-${entry.Id}`
                          }
                          onPress={() =>
                            toggleUser(
                              entry,
                              "IsDisabled",
                              !entry.Policy?.IsDisabled,
                            )
                          }
                        />
                        <AdminAction
                          label={
                            actionPending === `admin-${entry.Id}`
                              ? "Saving..."
                              : entry.Policy?.IsAdministrator
                                ? "Remove admin"
                                : "Make admin"
                          }
                          warning={entry.Policy?.IsAdministrator}
                          confirmed={confirmation === `admin-${entry.Id}`}
                          onPress={() =>
                            toggleUser(
                              entry,
                              "IsAdministrator",
                              !entry.Policy?.IsAdministrator,
                            )
                          }
                        />
                        {entry.Id && entry.Id !== credentials.userId ? (
                          <AdminAction
                            label={
                              confirmation === `delete-user-${entry.Id}`
                                ? "Confirm delete"
                                : "Delete"
                            }
                            warning
                            confirmed={
                              confirmation === `delete-user-${entry.Id}`
                            }
                            onPress={() =>
                              confirmAction(
                                `delete-user-${entry.Id}`,
                                () =>
                                  runAdminDelete(
                                    credentials,
                                    `/Users/${encodeURIComponent(entry.Id!)}`,
                                  ),
                                `${entry.Name ?? "User"} deleted.`,
                              )
                            }
                          />
                        ) : null}
                      </View>
                    </View>
                  )}
                />
                {passwordUser?.Id ? (
                  <View style={styles.passwordEditor}>
                    <Text style={styles.sessionTitle}>
                      Reset password for {passwordUser.Name ?? "user"}
                    </Text>
                    <Text style={styles.sessionMeta}>
                      Leave the field empty to clear the password.
                    </Text>
                    <TextInput
                      value={passwordDraft}
                      onChangeText={setPasswordDraft}
                      autoCapitalize="none"
                      autoCorrect={false}
                      secureTextEntry
                      selectionColor="#67e8f9"
                      style={styles.configInput}
                    />
                    <View style={styles.systemActions}>
                      <AdminAction
                        label={
                          actionPending === `password-${passwordUser.Id}`
                            ? "Saving..."
                            : "Save password"
                        }
                        warning={passwordDraft.length === 0}
                        confirmed={
                          confirmation === `password-${passwordUser.Id}`
                        }
                        onPress={() => {
                          const key = `password-${passwordUser.Id}`;
                          const action = () =>
                            updateAdminUserPassword(
                              credentials,
                              passwordUser.Id!,
                              passwordDraft,
                            );
                          const success = `${passwordUser.Name ?? "User"} password updated.`;
                          if (!passwordDraft)
                            confirmAction(key, action, success);
                          else void performAction(key, action, success);
                        }}
                      />
                      <AdminAction
                        label="Cancel"
                        onPress={() => setPasswordUser(null)}
                      />
                    </View>
                  </View>
                ) : null}
              </Panel>
            ) : item === "libraries" ? (
              <Panel
                title="Libraries"
                description="Jellyfin virtual folders and configured library sources."
              >
                <View style={styles.createUserForm}>
                  <View style={styles.formInputWrap}>
                    <Text style={styles.key}>LIBRARY NAME</Text>
                    <TextInput
                      value={libraryName}
                      onChangeText={setLibraryName}
                      selectionColor="#67e8f9"
                      style={styles.configInput}
                    />
                  </View>
                  <View style={styles.formInputWrap}>
                    <Text style={styles.key}>COLLECTION TYPE</Text>
                    <TextInput
                      value={libraryType}
                      onChangeText={setLibraryType}
                      autoCapitalize="none"
                      autoCorrect={false}
                      selectionColor="#67e8f9"
                      style={styles.configInput}
                    />
                  </View>
                  <View style={styles.formInputWrap}>
                    <Text style={styles.key}>MEDIA PATH</Text>
                    <TextInput
                      value={libraryPath}
                      onChangeText={setLibraryPath}
                      autoCapitalize="none"
                      autoCorrect={false}
                      selectionColor="#67e8f9"
                      style={styles.configInput}
                    />
                  </View>
                  <AdminAction
                    label={
                      actionPending === "add-library"
                        ? "Adding..."
                        : "Add library"
                    }
                    onPress={() =>
                      void performAction(
                        "add-library",
                        () =>
                          addAdminLibrary(
                            credentials,
                            libraryName,
                            libraryType,
                            libraryPath,
                          ),
                        `${libraryName.trim() || "Library"} added.`,
                      ).then(() => {
                        setLibraryName("");
                        setLibraryPath("");
                      })
                    }
                  />
                  <AdminAction
                    label={
                      actionPending === "scan-libraries"
                        ? "Starting scan..."
                        : "Scan all libraries"
                    }
                    onPress={() =>
                      void performAction(
                        "scan-libraries",
                        () => runAdminPost(credentials, "/Library/Refresh"),
                        "Library scan started.",
                      )
                    }
                  />
                </View>
                {data.libraries.length ? (
                  <FlatList
                    data={data.libraries}
                    keyExtractor={(entry, index) =>
                      entry.Id ?? entry.Name ?? `library-${index}`
                    }
                    scrollEnabled={false}
                    renderItem={({ item: entry }) => (
                      <View style={[styles.session, compact && styles.sessionCompact]}>
                        <View style={[styles.sessionCopy, compact && styles.sessionCopyCompact]}>
                          <Text style={styles.sessionTitle}>
                            {entry.Name ?? "Library"}
                          </Text>
                          <Text style={styles.sessionMeta}>
                            {[
                              entry.CollectionType ?? entry.Type ?? "Virtual folder",
                              ...(entry.Locations ?? []),
                            ].join(" · ")}
                          </Text>
                        </View>
                        <View style={[styles.rowActions, compact && styles.rowActionsCompact]}>
                          <Text style={styles.infoBadge}>AVAILABLE</Text>
                          {entry.Name ? (
                            <AdminAction
                              label={
                                confirmation === `library-${entry.Name}`
                                  ? "Confirm remove"
                                  : "Remove"
                              }
                              warning
                              confirmed={
                                confirmation === `library-${entry.Name}`
                              }
                              onPress={() =>
                                confirmAction(
                                  `library-${entry.Name}`,
                                  () =>
                                    removeAdminLibrary(
                                      credentials,
                                      entry.Name!,
                                    ),
                                  `${entry.Name} removed.`,
                                )
                              }
                            />
                          ) : null}
                        </View>
                      </View>
                    )}
                  />
                ) : (
                  <Text style={styles.empty}>No libraries returned.</Text>
                )}
              </Panel>
            ) : item === "all-sessions" ? (
              <Panel
                title={selectedLabel}
                description="Connected clients, devices, and current playback state."
              >
                {data.sessions.length ? (
                  <FlatList
                    data={data.sessions}
                    keyExtractor={(entry, index) =>
                      entry.Id ?? `session-${index}`
                    }
                    scrollEnabled={false}
                    renderItem={({ item: entry }) => (
                      <View style={[styles.session, compact && styles.sessionCompact]}>
                        <View style={[styles.sessionCopy, compact && styles.sessionCopyCompact]}>
                          <Text style={styles.sessionTitle}>
                            {entry.DeviceName ?? entry.Client ?? "Device"}
                          </Text>
                          <Text style={styles.sessionMeta}>
                            {[
                              entry.UserName,
                              entry.Client,
                              entry.NowPlayingItem?.Name,
                            ]
                              .filter(Boolean)
                              .join(" · ")}
                          </Text>
                        </View>
                        <Text
                          style={
                            entry.NowPlayingItem
                              ? styles.goodBadge
                              : styles.infoBadge
                          }
                        >
                          {entry.NowPlayingItem
                            ? entry.PlayState?.IsPaused
                              ? "PAUSED"
                              : "PLAYING"
                            : "CONNECTED"}
                        </Text>
                      </View>
                    )}
                  />
                ) : (
                  <Text style={styles.empty}>No connected sessions.</Text>
                )}
              </Panel>
            ) : item === "all-tasks" ? (
              <Panel
                title="Scheduled Tasks"
                description="Maintenance jobs and their latest execution status."
              >
                <FlatList
                  data={data.tasks}
                  keyExtractor={(entry, index) =>
                    entry.Id ?? entry.Key ?? `task-${index}`
                  }
                  scrollEnabled={false}
                  renderItem={({ item: entry }) => (
                    <View style={[styles.session, compact && styles.sessionCompact]}>
                      <View style={[styles.sessionCopy, compact && styles.sessionCopyCompact]}>
                        <Text style={styles.sessionTitle}>
                          {entry.Name ?? entry.Key ?? "Task"}
                        </Text>
                        <Text style={styles.sessionMeta}>
                          {entry.LastExecutionResult?.EndTimeUtc
                            ? new Date(
                                entry.LastExecutionResult.EndTimeUtc,
                              ).toLocaleString()
                            : "Not run"}
                        </Text>
                      </View>
                      <View style={[styles.rowActions, compact && styles.rowActionsCompact]}>
                        <Text
                          style={
                            String(entry.LastExecutionResult?.Status ?? "")
                              .toLowerCase()
                              .includes("fail")
                              ? styles.warnBadge
                              : styles.infoBadge
                          }
                        >
                          {entry.State === "Running"
                            ? "RUNNING"
                            : (entry.LastExecutionResult?.Status?.toUpperCase() ??
                              "IDLE")}
                        </Text>
                        {entry.Id ? (
                          <AdminAction
                            label={
                              actionPending === `task-${entry.Id}`
                                ? entry.State === "Running"
                                  ? "Stopping..."
                                  : "Starting..."
                                : entry.State === "Running"
                                  ? "Stop"
                                  : "Run"
                            }
                            warning={entry.State === "Running"}
                            onPress={() =>
                              void performAction(
                                `task-${entry.Id}`,
                                () =>
                                  entry.State === "Running"
                                    ? runAdminDelete(
                                        credentials,
                                        `/ScheduledTasks/Running/${encodeURIComponent(entry.Id!)}`,
                                      )
                                    : runAdminPost(
                                        credentials,
                                        `/ScheduledTasks/Running/${encodeURIComponent(entry.Id!)}`,
                                      ),
                                `${entry.Name ?? "Task"} ${entry.State === "Running" ? "stopped" : "started"}.`,
                              )
                            }
                          />
                        ) : null}
                      </View>
                    </View>
                  )}
                />
              </Panel>
            ) : item === "api-keys" ? (
              <Panel
                title="API Keys"
                description="Create integration credentials and revoke keys that are no longer trusted."
              >
                <View style={styles.createUserForm}>
                  <View style={styles.formInputWrap}>
                    <Text style={styles.key}>APPLICATION NAME</Text>
                    <TextInput
                      value={apiKeyName}
                      onChangeText={setApiKeyName}
                      autoCapitalize="none"
                      autoCorrect={false}
                      selectionColor="#67e8f9"
                      style={styles.configInput}
                    />
                  </View>
                  <AdminAction
                    label={
                      actionPending === "create-api-key"
                        ? "Creating..."
                        : "Create API key"
                    }
                    onPress={() => {
                      const name = apiKeyName.trim();
                      if (!name) {
                        setActionMessage("Enter an application name.");
                        return;
                      }
                      void performAction(
                        "create-api-key",
                        () =>
                          runAdminPost(
                            credentials,
                            `/Auth/Keys?app=${encodeURIComponent(name)}`,
                          ),
                        `${name} API key created.`,
                      ).then(() => {
                        setApiKeyName("");
                        void sectionQuery.refetch();
                      });
                    }}
                  />
                </View>
                {sectionQuery.isLoading ? (
                  <ActivityIndicator color="#67e8f9" />
                ) : sectionQuery.data?.length ? (
                  <FlatList
                    data={sectionQuery.data}
                    keyExtractor={(entry, index) =>
                      String(entry.Id ?? entry.AccessToken ?? `key-${index}`)
                    }
                    scrollEnabled={false}
                    renderItem={({ item: entry }) => {
                      const token = String(
                        entry.AccessToken ?? entry.Token ?? entry.Key ?? "",
                      );
                      return (
                        <View style={[styles.session, compact && styles.sessionCompact]}>
                          <View style={[styles.sessionCopy, compact && styles.sessionCopyCompact]}>
                            <Text style={styles.sessionTitle}>
                              {String(entry.Name ?? "API key")}
                            </Text>
                            <Text numberOfLines={1} style={styles.sessionMeta}>
                              {token
                                ? `${token.slice(0, 8)}${token.length > 8 ? "..." : ""}`
                                : "Token hidden by server"}
                            </Text>
                          </View>
                          {token ? (
                            <AdminAction
                              label={
                                confirmation === `key-${token}`
                                  ? "Confirm revoke"
                                  : "Revoke"
                              }
                              warning
                              confirmed={confirmation === `key-${token}`}
                              onPress={() =>
                                confirmAction(
                                  `key-${token}`,
                                  () =>
                                    runAdminDelete(
                                      credentials,
                                      `/Auth/Keys/${encodeURIComponent(token)}`,
                                    ),
                                  "API key revoked.",
                                )
                              }
                            />
                          ) : null}
                        </View>
                      );
                    }}
                  />
                ) : (
                  <Text style={styles.empty}>No API keys returned.</Text>
                )}
              </Panel>
            ) : item === "generic" ? (
              <Panel
                title={selectedLabel}
                description="Configuration and records returned by Jellyfin for this section."
              >
                {CONFIG_FIELDS[selected] && configQuery.data ? (
                  <ConfigurationEditor
                    section={selected}
                    document={configQuery.data}
                    saving={actionPending === `config-${selected}`}
                    onSave={(value) =>
                      void performAction(
                        `config-${selected}`,
                        () => saveAdminConfiguration(credentials, selected, value),
                        `${selectedLabel} configuration saved.`,
                      ).then(() => configQuery.refetch())
                    }
                  />
                ) : sectionQuery.isLoading || configQuery.isLoading ? (
                  <ActivityIndicator color="#67e8f9" />
                ) : sectionQuery.isError ? (
                  <Text style={styles.errorCopy}>
                    This Jellyfin endpoint is unavailable.
                  </Text>
                ) : sectionQuery.data?.length ? (
                  <FlatList
                    data={sectionQuery.data}
                    keyExtractor={(_, index) => `${selected}-${index}`}
                    scrollEnabled={false}
                    renderItem={({ item: entry }) => {
                      const preview = previewRecord(entry);
                      return (
                        <View style={styles.record}>
                          <Text style={styles.sessionTitle}>
                            {preview.title}
                          </Text>
                          <Text numberOfLines={3} style={styles.sessionMeta}>
                            {preview.detail}
                          </Text>
                        </View>
                      );
                    }}
                  />
                ) : (
                  <Text style={styles.empty}>
                    Jellyfin returned no records for this section.
                  </Text>
                )}
              </Panel>
            ) : item === "api-console" ? (
              <Panel
                title="Admin Endpoint Console"
                description="Run a read-only Jellyfin API request with the current administrator token."
              >
                <Text style={styles.key}>API PATH</Text>
                <TextInput
                  value={consolePath}
                  onChangeText={setConsolePath}
                  autoCapitalize="none"
                  autoCorrect={false}
                  selectionColor="#67e8f9"
                  style={styles.consoleInput}
                />
                <FocusablePressable
                  accessibilityLabel="Run admin API request"
                  onPress={() => {
                    if (consolePending) return;
                    setConsolePending(true);
                    setConsoleResult("");
                    void runAdminGet(credentials, consolePath)
                      .then((value) =>
                        setConsoleResult(JSON.stringify(value, null, 2)),
                      )
                      .catch((error) =>
                        setConsoleResult(
                          error instanceof Error
                            ? error.message
                            : "Request failed.",
                        ),
                      )
                      .finally(() => setConsolePending(false));
                  }}
                  style={(focused, pressed, hovered) => [
                    styles.refresh,
                    styles.consoleButton,
                    (focused || hovered) && styles.refreshActive,
                    pressed && styles.pressed,
                  ]}
                >
                  <Text style={styles.refreshText}>
                    {consolePending ? "Running..." : "Run GET Request"}
                  </Text>
                </FocusablePressable>
                {consoleResult ? (
                  <Text selectable style={styles.consoleOutput}>
                    {consoleResult}
                  </Text>
                ) : null}
              </Panel>
            ) : (
              <Panel
                title={selectedLabel}
                description="This dashboard section is not yet migrated."
              >
                <Text style={styles.empty}>
                  The overview is available while this section is implemented
                  atomically.
                </Text>
              </Panel>
            )
          }
          ItemSeparatorComponent={() => <View style={styles.panelGap} />}
        />
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1, backgroundColor: "#080808" },
  shell: { flex: 1, flexDirection: "row" },
  shellCompact: { flexDirection: "column" },
  sidebar: {
    width: 320,
    padding: 16,
    borderRightWidth: 1,
    borderRightColor: "rgba(255,255,255,0.1)",
    backgroundColor: "#0b0b0b",
  },
  sidebarCompact: {
    width: "auto",
    paddingTop: 14,
    borderRightWidth: 0,
    borderBottomWidth: 1,
    borderBottomColor: "rgba(255,255,255,0.1)",
  },
  brandButton: { alignSelf: "flex-start" },
  brand: { color: "#fff", fontSize: 20, lineHeight: 25, fontWeight: "900" },
  adminLabel: {
    marginTop: 4,
    color: "rgba(207,250,254,0.65)",
    fontSize: 11,
    lineHeight: 15,
    fontWeight: "900",
  },
  nav: { marginTop: 20, gap: 5 },
  navCompact: {
    marginTop: 10,
    paddingBottom: 4,
    flexDirection: "row",
  },
  navList: { flex: 1 },
  navListCompact: { flex: 0, height: 48, flexShrink: 0 },
  navGroup: {
    marginTop: 13,
    marginBottom: 5,
    paddingHorizontal: 4,
    color: "rgba(207,250,254,0.38)",
    fontSize: 9,
    lineHeight: 13,
    fontWeight: "900",
    letterSpacing: 1.1,
  },
  navItem: {
    minHeight: 36,
    paddingHorizontal: 12,
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.1)",
    borderRadius: 6,
    justifyContent: "center",
    backgroundColor: "rgba(255,255,255,0.04)",
  },
  navItemSelected: {
    borderColor: "rgba(103,232,249,0.45)",
    backgroundColor: "rgba(103,232,249,0.1)",
  },
  navItemActive: { borderColor: "rgba(255,255,255,0.25)" },
  navText: {
    color: "rgba(255,255,255,0.65)",
    fontSize: 13,
    lineHeight: 18,
    fontWeight: "800",
  },
  navTextSelected: { color: "#fff" },
  content: { flexGrow: 1, padding: 28, paddingBottom: 72 },
  header: {
    marginBottom: 24,
    paddingBottom: 20,
    borderBottomWidth: 1,
    borderBottomColor: "rgba(255,255,255,0.1)",
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 16,
  },
  headerCopy: { flex: 1 },
  title: {
    marginTop: 7,
    color: "#fff",
    fontSize: 32,
    lineHeight: 38,
    fontWeight: "900",
  },
  subtitle: {
    marginTop: 8,
    maxWidth: 900,
    color: "rgba(255,255,255,0.58)",
    fontSize: 13,
    lineHeight: 19,
    fontWeight: "600",
  },
  loaded: {
    marginTop: 10,
    color: "rgba(255,255,255,0.38)",
    fontSize: 11,
    lineHeight: 15,
    fontWeight: "700",
  },
  actionMessage: {
    marginTop: 8,
    color: "#a5f3fc",
    fontSize: 11,
    lineHeight: 15,
    fontWeight: "700",
  },
  refresh: {
    minHeight: 38,
    paddingHorizontal: 13,
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.16)",
    borderRadius: 6,
    flexDirection: "row",
    alignItems: "center",
    gap: 7,
    backgroundColor: "rgba(255,255,255,0.04)",
  },
  refreshActive: {
    borderColor: "rgba(255,255,255,0.35)",
    backgroundColor: "rgba(255,255,255,0.1)",
  },
  refreshText: { color: "#ddd", fontSize: 12, fontWeight: "800" },
  stats: { gap: 10, marginBottom: 20 },
  statRow: { gap: 10 },
  stat: {
    flex: 1,
    minHeight: 94,
    marginBottom: 10,
    padding: 13,
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.1)",
    borderRadius: 7,
    backgroundColor: "rgba(255,255,255,0.04)",
  },
  statCyan: {
    borderColor: "rgba(103,232,249,0.25)",
    backgroundColor: "rgba(103,232,249,0.07)",
  },
  statGreen: {
    borderColor: "rgba(74,222,128,0.22)",
    backgroundColor: "rgba(74,222,128,0.06)",
  },
  statAmber: {
    borderColor: "rgba(251,191,36,0.25)",
    backgroundColor: "rgba(251,191,36,0.07)",
  },
  statLabel: {
    color: "rgba(255,255,255,0.4)",
    fontSize: 10,
    lineHeight: 14,
    fontWeight: "900",
  },
  statValue: {
    marginTop: 5,
    color: "#fff",
    fontSize: 21,
    lineHeight: 27,
    fontWeight: "900",
  },
  statDetail: {
    marginTop: 3,
    color: "rgba(255,255,255,0.48)",
    fontSize: 11,
    lineHeight: 15,
  },
  panel: {
    padding: 18,
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.1)",
    borderRadius: 7,
    backgroundColor: "rgba(255,255,255,0.035)",
  },
  panelTitle: {
    color: "#fff",
    fontSize: 19,
    lineHeight: 25,
    fontWeight: "900",
  },
  panelDescription: {
    marginTop: 4,
    color: "rgba(255,255,255,0.5)",
    fontSize: 12,
    lineHeight: 17,
  },
  panelBody: { marginTop: 15, gap: 8 },
  panelGap: { height: 18 },
  session: {
    minHeight: 58,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: "rgba(255,255,255,0.08)",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  sessionCopy: { flex: 1 },
  sessionCompact: {
    paddingVertical: 13,
    flexDirection: "column",
    alignItems: "stretch",
  },
  sessionCopyCompact: { width: "auto", flex: 0 },
  rowActions: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    flexWrap: "wrap",
    gap: 7,
  },
  rowActionsCompact: {
    width: "auto",
    marginTop: 9,
    justifyContent: "flex-start",
  },
  actionButton: {
    minHeight: 30,
    paddingHorizontal: 10,
    borderWidth: 1,
    borderColor: "rgba(103,232,249,0.28)",
    borderRadius: 5,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(103,232,249,0.08)",
  },
  actionButtonWarning: {
    borderColor: "rgba(251,191,36,0.3)",
    backgroundColor: "rgba(251,191,36,0.07)",
  },
  actionButtonConfirmed: {
    borderColor: "#fbbf24",
    backgroundColor: "rgba(251,191,36,0.2)",
  },
  actionButtonActive: {
    borderColor: "rgba(255,255,255,0.65)",
    backgroundColor: "rgba(255,255,255,0.12)",
  },
  actionText: { color: "#cffafe", fontSize: 10, fontWeight: "900" },
  actionTextWarning: { color: "#fde68a" },
  sessionTitle: {
    color: "#fff",
    fontSize: 13,
    lineHeight: 18,
    fontWeight: "800",
  },
  sessionMeta: {
    marginTop: 3,
    color: "rgba(255,255,255,0.45)",
    fontSize: 11,
    lineHeight: 15,
  },
  goodBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 20,
    color: "#86efac",
    fontSize: 9,
    fontWeight: "900",
    backgroundColor: "rgba(74,222,128,0.12)",
  },
  infoBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 20,
    color: "#a5f3fc",
    fontSize: 9,
    fontWeight: "900",
    backgroundColor: "rgba(103,232,249,0.12)",
  },
  warnBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 20,
    color: "#fde68a",
    fontSize: 9,
    fontWeight: "900",
    backgroundColor: "rgba(251,191,36,0.12)",
  },
  keyValue: {
    minHeight: 48,
    paddingVertical: 9,
    borderBottomWidth: 1,
    borderBottomColor: "rgba(255,255,255,0.08)",
  },
  systemActions: {
    marginTop: 12,
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 8,
  },
  activityDate: {
    maxWidth: 180,
    color: "rgba(255,255,255,0.38)",
    fontSize: 10,
    lineHeight: 14,
    fontWeight: "700",
    textAlign: "right",
  },
  key: {
    color: "rgba(255,255,255,0.38)",
    fontSize: 10,
    lineHeight: 14,
    fontWeight: "900",
  },
  value: {
    marginTop: 3,
    color: "rgba(255,255,255,0.82)",
    fontSize: 13,
    lineHeight: 18,
    fontWeight: "700",
  },
  record: {
    minHeight: 56,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: "rgba(255,255,255,0.08)",
  },
  configEditor: { gap: 14 },
  createUserForm: {
    marginBottom: 12,
    paddingBottom: 14,
    borderBottomWidth: 1,
    borderBottomColor: "rgba(255,255,255,0.1)",
    flexDirection: "row",
    alignItems: "flex-end",
    flexWrap: "wrap",
    gap: 10,
  },
  formInputWrap: { minWidth: 180, flex: 1, gap: 6 },
  passwordEditor: {
    marginTop: 14,
    padding: 14,
    borderWidth: 1,
    borderColor: "rgba(103,232,249,0.22)",
    borderRadius: 6,
    gap: 8,
    backgroundColor: "rgba(103,232,249,0.04)",
  },
  configField: { marginBottom: 12, gap: 6 },
  configBoolean: {
    minHeight: 50,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: "rgba(255,255,255,0.08)",
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  configInput: {
    minHeight: 40,
    paddingHorizontal: 11,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.15)",
    borderRadius: 5,
    color: "#f4f4f5",
    fontSize: 12,
    lineHeight: 17,
    backgroundColor: "rgba(0,0,0,0.25)",
  },
  configInputMultiline: { minHeight: 112, verticalAlign: "top" },
  consoleInput: {
    minHeight: 42,
    paddingHorizontal: 12,
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.16)",
    borderRadius: 6,
    color: "#fff",
    fontSize: 13,
    fontFamily: "monospace",
    backgroundColor: "rgba(255,255,255,0.04)",
  },
  consoleButton: { alignSelf: "flex-start", marginTop: 4 },
  consoleOutput: {
    marginTop: 10,
    padding: 12,
    borderRadius: 6,
    color: "#a5f3fc",
    fontSize: 11,
    lineHeight: 16,
    fontFamily: "monospace",
    backgroundColor: "rgba(0,0,0,0.4)",
  },
  empty: { color: "rgba(255,255,255,0.48)", fontSize: 12, lineHeight: 18 },
  center: {
    flex: 1,
    padding: 24,
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
  },
  loading: { color: "rgba(255,255,255,0.65)", fontSize: 14 },
  errorTitle: { color: "#fff", fontSize: 24, fontWeight: "900" },
  errorCopy: {
    color: "rgba(255,255,255,0.55)",
    fontSize: 14,
    textAlign: "center",
  },
  returnButton: {
    minHeight: 40,
    paddingHorizontal: 14,
    borderRadius: 6,
    justifyContent: "center",
    backgroundColor: "rgba(103,232,249,0.14)",
  },
  returnText: { color: "#cffafe", fontSize: 13, fontWeight: "800" },
  pressed: { opacity: 0.75 },
});
